import React, {ChangeEvent, ReactNode, useEffect, useState} from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'

import {cssTheme} from '../../../themes'
import {Badge} from '../Badge'
import {
  CaptionStyles,
  FieldStyles,
  FieldWrapperStyles,
  InputGroupStyles,
  InputLabel,
  InputLabelText,
  InputTextStyles
} from '../InputText/styles'

interface Props {
  id: string
  name: string
  value?: string | number
  ariaLabel?: React.AriaAttributes['aria-label']
  ariaDescribedBy?: React.AriaAttributes['aria-describedby']
  label?: string
  caption?: ReactNode
  isValid?: boolean
  isInvalid?: boolean
  isPrivate?: boolean
  onChange?: (value: string, event: ChangeEvent<HTMLTextAreaElement>) => void
  onFocus?: React.FocusEventHandler<HTMLTextAreaElement>
  onBlur?: React.FocusEventHandler<HTMLTextAreaElement>
  autoComplete?: string
  className?: string
  dataId?: string
  textTransformType?: React.CSSProperties['textTransform']
  inputHeight?: number
  isResizable?: boolean
  disabled?: boolean
  required?: boolean
  readOnly?: boolean
}

const DEFAULT_INPUT_HEIGHT = 124

const Label = styled.label`
  ${FieldStyles}

  position: relative;
  padding: 0;
  padding-inline-start: ${cssTheme.layout.spacing.s400};
  align-items: flex-start;
  height: auto;
  gap: ${cssTheme.layout.spacing.s500};
  &:focus-within,
  &.--is-filled {
    .label {
      top: -9px;
      padding: 0 ${cssTheme.layout.spacing.s200};
      transform: translateX(-4px);
      &.--is-invalid {
        color: ${cssTheme.colors.input.error.caption};
      }
    }
  }
  &.--is-resizable {
    textarea {
      resize: vertical;
      &:disabled {
        resize: none;
      }
    }
  }
  &.--is-invalid {
    color: ${cssTheme.colors.input.default.text};
    &:disabled {
      background: none;
      color: ${cssTheme.colors.input.disabled.caption};
    }
  }
`

const BadgeWithIcon = styled(Badge)`
  margin-top: ${cssTheme.layout.spacing.s400};
  position: absolute;
  right: ${cssTheme.layout.spacing.s400};
`

const Textarea = styled.textarea`
  border: none;
  background: none;
  outline: none;
  box-shadow: none;
  margin: 0;
  min-height: ${DEFAULT_INPUT_HEIGHT}px;
  border-radius: ${cssTheme.layout.radius.md};
  padding: ${cssTheme.layout.spacing.s300} 0 !important;
  padding-inline-end: ${cssTheme.layout.spacing.s800} !important;
`

const CustomInputLabel = styled(InputLabel)`
  display: flex;
  align-items: flex-start;
  padding: ${cssTheme.layout.spacing.s300} 0;
  max-width: calc(100% - ${cssTheme.layout.spacing.s800});

  &.--is-disabled {
    color: ${cssTheme.colors.input.disabled.caption};
  }
`

export const InputTextarea = ({
  id,
  ariaLabel,
  ariaDescribedBy,
  name,
  value,
  label,
  caption,
  isValid = false,
  isInvalid = false,
  isPrivate = false,
  onChange,
  onFocus,
  onBlur,
  autoComplete,
  className,
  dataId,
  textTransformType,
  inputHeight = DEFAULT_INPUT_HEIGHT,
  isResizable = true,
  disabled = false,
  required = false,
  readOnly = false
}: Props) => {
  const [isFilled, setIsFilled] = useState(Boolean(value))

  useEffect(() => {
    setIsFilled(Boolean(value))
  }, [value])

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setIsFilled(Boolean(event.target.value))
    onChange?.(event.target.value, event)
  }

  const handleFocus = (event: React.FocusEvent<HTMLTextAreaElement>) => {
    onFocus?.(event)
  }

  const handleBlur = (event: React.FocusEvent<HTMLTextAreaElement>) => {
    onBlur?.(event)
  }

  return (
    <div css={FieldWrapperStyles} className={className}>
      <Label
        htmlFor={id}
        className={cx(
          isInvalid && '--is-invalid',
          isFilled && '--is-filled',
          disabled && '--is-disabled',
          isResizable && '--is-resizable',
          readOnly && '--is-read-only'
        )}
      >
        <div css={InputGroupStyles}>
          <Textarea
            aria-label={ariaLabel}
            aria-describedby={ariaDescribedBy}
            aria-invalid={isInvalid}
            id={id}
            name={name}
            value={value}
            disabled={disabled}
            readOnly={readOnly}
            required={required}
            autoComplete={autoComplete}
            data-id={dataId}
            css={InputTextStyles}
            className={cx(isPrivate && 'fs-mask')}
            style={{
              textTransform: textTransformType,
              height: inputHeight
            }}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
          {label && (
            <CustomInputLabel
              className={cx(
                'label',
                disabled && '--is-disabled',
                isInvalid && '--is-invalid'
              )}
            >
              <InputLabelText>{label}</InputLabelText>
              {required && <span className="asterisk">*</span>}
            </CustomInputLabel>
          )}
        </div>
        {isValid && (
          <BadgeWithIcon size="xs" variant="success" iconName="Checkmark" />
        )}
        {isInvalid && (
          <BadgeWithIcon size="xs" variant="danger" iconName="WarningMark" />
        )}
      </Label>
      {caption && (
        <div
          css={CaptionStyles}
          className={cx(
            isInvalid && '--is-invalid',
            disabled && '--is-disabled'
          )}
        >
          {caption}
        </div>
      )}
    </div>
  )
}
